import { sectionRenderer, getFlavourShopURL, getCleanCatalogueURL, getNativeCatalogueURL } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { debounce, mediaQueryLarge, startViewTransition } from '@theme/utilities';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';
import { CollectionUpdateEvent, SearchUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';
const APPLICATION_FILTER = 'filter.p.m.custom.applications';
const BEST_SELLERS_FILTER = 'wf_best_sellers';

// One application can have several translated native values. Keep that OR
// group intact while preserving the existing single-application interaction.
function getApplicationGroup(root, value) {
  const first = value.split(',')[0];
  const radio = [...root.querySelectorAll(`input[type="radio"][name="${APPLICATION_FILTER}"]`)]
    .find(input => input.value.split(',').includes(first));
  return radio?.value ?? first;
}

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('popstate', this.#restoreApplicationFilters);
    queueMicrotask(() => this.#initializeBestSellers());
    // In vertical filter mode the in-page filters take over at ≥750px, so a
    // drawer left open across a mobile→desktop resize would render duplicate
    // filter UI. `data-filter-style` is only set on the drawer variant (see
    // blocks/filters.liquid), so matching "vertical" here uniquely targets it.
    if (this.dataset.filterStyle === 'vertical') {
      mediaQueryLarge.addEventListener('change', this.#onBreakpointChange);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('popstate', this.#restoreApplicationFilters);
    mediaQueryLarge.removeEventListener('change', this.#onBreakpointChange);
  }

  #onBreakpointChange = (/** @type {MediaQueryListEvent} */ event) => {
    if (!event.matches) return;
    const drawer = document.getElementById('filters-drawer');
    if (drawer && 'close' in drawer && typeof drawer.close === 'function') {
      drawer.close();
    }
  };

  #restoreApplicationFilters = () => {
    // Desktop and drawer forms share a results section. Refresh it only once
    // when Back/Forward restores a previous application selection.
    const results = this.closest('results-list');
    if (results?.querySelector('facets-form-component') !== this) return;
    if (!this.querySelector('[data-single-application], wf-best-sellers-filter')) return;
    if (this.#fallbackCollectionURL) return;

    const renderPromise = this.#updateSection();
    if (this.dataset.pageType === 'search') this.#dispatchSearchUpdateEvent(renderPromise);
    else this.#dispatchCollectionUpdateEvent(renderPromise);
  };

  #initializeBestSellers() {
    if (!this.isConnected || !this.querySelector('wf-best-sellers-filter')) return;
    if (this.closest('results-list')?.querySelector('facets-form-component') !== this) return;
    // Old collection links enter the same filter without another page load.
    const url = getFlavourShopURL(new URL(window.location.href));
    if (url.href !== window.location.href) history.replaceState(history.state, '', url.href);
    // A shared URL or refresh initially contains Shopify's unfiltered HTML.
    // Fetch the complete filtered section, including its Application guide.
    if (url.searchParams.get(BEST_SELLERS_FILTER) === '1' && !this.hasAttribute('data-wf-options-merged')) {
      if (this.#fallbackCollectionURL) return;
      // Initial results should not wait for a visual transition snapshot.
      const renderPromise = this.#updateSection({ transition: false });
      this.#dispatchCollectionUpdateEvent(renderPromise);
    }
  }

  /**
   * Creates URL parameters from form data
   * @param {FormData | URLSearchParams} [formData] - Optional parameters to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');
    if (newParameters.get(BEST_SELLERS_FILTER) !== '1') newParameters.delete(BEST_SELLERS_FILTER);
    else newParameters.set(BEST_SELLERS_FILTER, '1');

    const previewTheme = new URL(window.location.href).searchParams.get('preview_theme_id');
    if (previewTheme) newParameters.set('preview_theme_id', previewTheme);

    // The empty radio means All applications. Other filter groups retain
    // their multiple values when this choice is cleared or replaced.
    const application = newParameters.get(APPLICATION_FILTER);
    if (application === '') newParameters.delete(APPLICATION_FILTER);
    else if (application !== null) {
      // Repeated parameters preserve Shopify's OR semantics. Encoding the
      // comma in a combined value can instead request a literal value.
      newParameters.delete(APPLICATION_FILTER);
      for (const value of getApplicationGroup(this, application).split(',')) newParameters.append(APPLICATION_FILTER, value);
    }

    const searchQuery = this.#getSearchQuery();
    if (this.#fallbackCollectionURL) {
      // These facets describe the catalogue, not the unsuccessful search.
      newParameters.delete(SEARCH_QUERY);
      newParameters.delete('type');
      newParameters.delete('options[prefix]');
    } else if (searchQuery) {
      newParameters.set(SEARCH_QUERY, searchQuery);
      // Keep the search scope and prefix matching when the facets form only
      // submits filter fields, so counts still describe the visible products.
      const currentParameters = new URL(window.location.href).searchParams;
      for (const key of ['type', 'options[prefix]']) {
        const value = currentParameters.get(key);
        if (value !== null && !newParameters.has(key)) newParameters.set(key, value);
      }
    }

    return newParameters;
  }

  /**
   * Collection shown when a search has no matches. Its filters must run on the
   * collection route, where Shopify applies them to the displayed catalogue.
   * @returns {string | undefined}
   */
  get #fallbackCollectionURL() {
    return this.closest('results-list')?.dataset.fallbackCollectionUrl;
  }

  /**
   * Continue catalogue browsing on its native page, with the selected filters.
   * @param {URLSearchParams} parameters
   */
  #openFallbackCollection(parameters) {
    const collectionURL = this.#fallbackCollectionURL;
    if (!collectionURL) return;
    const url = new URL(collectionURL, window.location.origin);
    url.search = parameters.toString();
    // Retain an explicitly selected draft theme across the page navigation.
    const previewTheme = new URL(window.location.href).searchParams.get('preview_theme_id');
    if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
    window.location.assign(url.toString());
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = getFlavourShopURL(new URL(window.location.href));
    const urlParameters = this.createURLParameters();
    const sort = url.searchParams.get('sort_by');
    if (sort && !urlParameters.has('sort_by')) urlParameters.set('sort_by', sort);

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    const destination = getCleanCatalogueURL(url, { fromForm: true });
    history.pushState({ urlParameters: urlParameters.toString() }, '', destination.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = () => {
    if (this.#fallbackCollectionURL) {
      this.#openFallbackCollection(this.createURLParameters());
      return;
    }

    this.#updateURLHash();
    const renderPromise = this.#updateSection();
    if (this.dataset.pageType === 'search') {
      this.#dispatchSearchUpdateEvent(renderPromise);
    } else {
      this.#dispatchCollectionUpdateEvent(renderPromise);
    }
  };

  /**
   * Toggle membership like any other facet, retaining the Application and
   * other selections while refreshing both forms and the product results.
   * @param {boolean} selected
   */
  setBestSellers(selected) {
    const input = this.querySelector('wf-best-sellers-filter input');
    if (!(input instanceof HTMLInputElement)) return;
    input.checked = selected;
    this.updateFilters();
  }

  /**
   * Updates the section
   * @param {{ transition?: boolean }} [options]
   * @returns {Promise<void>}
   */
  #updateSection({ transition = true } = {}) {
    // Start the small count request alongside the results request, so the
    // refreshed forms can reuse it before the browser paints their counts.
    const bestSellers = this.querySelector('wf-best-sellers-filter');
    if (bestSellers instanceof BestSellersFilterComponent) bestSellers.prefetchCount();
    const viewTransition = transition && !this.closest('dialog');

    if (viewTransition) {
      return startViewTransition(() => sectionRenderer.renderSection(this.sectionId), ['product-grid']);
    } else {
      return sectionRenderer.renderSection(this.sectionId).then(() => {});
    }
  }

  /**
   * Dispatches search:update event if on the search page.
   * @param {Promise<void>} renderPromise - The promise from section rendering
   */
  #dispatchSearchUpdateEvent(renderPromise) {
    const query = this.#getSearchQuery();

    const deferredPromise = SearchUpdateEvent.createPromise();
    const productFilters = SearchUpdateEvent.parseProductFilters();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query,
          ...(productFilters && { productFilters }),
          sortKey: SearchUpdateEvent.getSortKey(),
        },
        promise: deferredPromise.promise,
      })
    );

    // Resolve promise after section renders with result count
    renderPromise
      .then(() => deferredPromise.resolve({ totalCount: this.#getResultsCount() }))
      .catch((error) => deferredPromise.reject(error));
  }

  /**
   * Gets the count of product results from the server-rendered data attribute.
   * @returns {number}
   */
  #getResultsCount() {
    return Number(this.dataset.resultsCount) || 0;
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   * @param {{ replace?: boolean }} [options] - Replace an obsolete history entry
   */
  updateFiltersByURL(url, { replace = false } = {}) {
    if (this.#fallbackCollectionURL) {
      const parameters = new URL(url, window.location.origin).searchParams;
      this.#openFallbackCollection(this.createURLParameters(parameters));
      return;
    }

    let targetURL = getFlavourShopURL(new URL(url, window.location.href));
    targetURL.search = this.createURLParameters(targetURL.searchParams).toString();
    targetURL = getCleanCatalogueURL(targetURL);
    if (targetURL.pathname !== new URL(window.location.href).pathname) {
      window.location.assign(targetURL.toString());
      return;
    }
    if (replace) history.replaceState(history.state, '', targetURL.toString());
    else history.pushState('', '', targetURL.toString());
    const renderPromise = this.#updateSection();
    if (this.dataset.pageType === 'search') {
      this.#dispatchSearchUpdateEvent(renderPromise);
    } else {
      this.#dispatchCollectionUpdateEvent(renderPromise);
    }
  }

  /**
   * Dispatches collection:update event if on a collection page.
   * @param {Promise<void>} renderPromise - The promise from section rendering
   */
  #dispatchCollectionUpdateEvent(renderPromise) {
    // Build collection object with available identifiers
    // Only use numeric IDs — non-numeric values like "all" fail GID validation
    /** @type {any} */
    const rawId = this.dataset.collectionId;
    const collectionData = {
      id: rawId && /^\d+$/.test(rawId) ? rawId : null,
      handle: /** @type {string} */ (this.dataset.collectionHandle),
      productsCount: this.#getResultsCount(),
    };

    const deferredPromise = CollectionUpdateEvent.createPromise();
    const productFilters = CollectionUpdateEvent.parseProductFilters();

    this.dispatchEvent(
      new CollectionUpdateEvent({
        collection: collectionData,
        ...(productFilters && { productFilters }),
        sortKey: CollectionUpdateEvent.getSortKey(),
        promise: deferredPromise.promise,
      })
    );

    // Resolve promise after section renders with product count
    renderPromise
      .then(() => deferredPromise.resolve({ productsCount: this.#getResultsCount() }))
      .catch((error) => deferredPromise.reject(error));
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/** Read the intersecting count from Shopify, never from the visible cards. */
class BestSellersFilterComponent extends HTMLElement {
  static observedAttributes = ['data-filter-state', 'data-count-needed'];
  #request = 0;
  #queued = false;

  connectedCallback() { this.#queueUpdate(); }
  attributeChangedCallback() { this.#queueUpdate(); }
  disconnectedCallback() { ++this.#request; }

  /** Fetch for the incoming URL, including Back/Forward before forms morph. */
  prefetchCount() {
    const current = getFlavourShopURL(new URL(window.location.href));
    if (current.searchParams.get(BEST_SELLERS_FILTER) === '1') return;
    const url = this.#countURL;
    if (!url || ![...url.searchParams].some(([name, value]) => name.startsWith('filter.') && value !== '')) return;
    // Do not hold up product results if this optional request is slow or fails.
    sectionRenderer.getSectionHTML('wf-best-sellers-count', true, url).catch(() => {});
  }

  /** One cache key for both forms, regardless of sorting or pagination. */
  get #countURL() {
    const form = this.closest('facets-form-component');
    const path = this.dataset.bestSellersUrl;
    if (!(form instanceof FacetsFormComponent) || !path) return;
    const url = new URL(path, window.location.origin);
    url.search = form.createURLParameters(getNativeCatalogueURL(new URL(window.location.href)).searchParams).toString();
    url.searchParams.delete('sort_by');
    return url;
  }

  #queueUpdate() {
    if (this.#queued) return;
    this.#queued = true;
    queueMicrotask(() => {
      this.#queued = false;
      this.#updateCount();
    });
  }

  async #updateCount() {
    const request = ++this.#request;
    if (!this.isConnected || this.dataset.countNeeded !== 'true') return;
    const count = this.querySelector('[data-best-sellers-count]');
    const input = this.querySelector('input');
    const url = this.#countURL;
    if (!(count instanceof HTMLElement) || !input || !url) return;
    count.hidden = true;

    try {
      const html = await sectionRenderer.getSectionHTML('wf-best-sellers-count', true, url);
      if (request !== this.#request || !this.isConnected) return;
      const document = new DOMParser().parseFromString(html, 'text/html');
      const value = document.querySelector('[data-wf-best-sellers-count]')?.getAttribute('data-wf-best-sellers-count');
      if (!value || !/^\d+$/.test(value)) return;
      count.textContent = `(${value})`;
      count.hidden = false;
      input.disabled = value === '0' && !input.checked;
      this.querySelector('.checkbox')?.classList.toggle('checkbox--disabled', input.disabled);
      // A later morph can require the count again even when its filters are
      // unchanged, such as a sort or pagination update using cached HTML.
      this.dataset.countNeeded = 'false';
    } catch {
      // A failed count request must not invent a zero or prevent navigation.
    }
  }
}

if (!customElements.get('wf-best-sellers-filter')) {
  customElements.define('wf-best-sellers-filter', BestSellersFilterComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Counts follow server-rendered URLs. Translated OR groups use a small count
 * section; All applications reuses the reset response and its product cache.
 */
class ApplicationCountComponent extends HTMLElement {
  static observedAttributes = ['data-count-url', 'data-count-section'];
  #request = 0;
  #queued = false;

  connectedCallback() {
    this.#queueUpdate();
  }

  attributeChangedCallback() {
    this.#queueUpdate();
  }

  disconnectedCallback() {
    ++this.#request;
  }

  #queueUpdate() {
    if (this.#queued) return;
    this.#queued = true;
    queueMicrotask(() => {
      this.#queued = false;
      this.#updateCount();
    });
  }

  async #updateCount() {
    const request = ++this.#request;
    const countURL = this.dataset.countUrl;
    if (!this.isConnected || !countURL) return;

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const url = new URL(countURL, window.location.origin);
    const previewTheme = new URL(window.location.href).searchParams.get('preview_theme_id');
    if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);

    try {
      // Share the section cache with the other form and reset prefetch.
      const countSection = this.dataset.countSection || facetsForm.sectionId;
      const html = await sectionRenderer.getSectionHTML(countSection, true, url);
      if (request !== this.#request || !this.isConnected || this.dataset.countUrl !== countURL) return;

      const document = new DOMParser().parseFromString(html, 'text/html');
      const value = document.querySelector('[data-wf-filter-count]')?.textContent?.trim()
        ?? document.querySelector('facets-form-component[data-results-count]')?.getAttribute('data-results-count');
      if (value == null || !/^\d+$/.test(value)) return;

      this.textContent = `(${value})`;
      this.hidden = false;
      delete this.dataset.countUrl;
    } catch {
      // A failed count request must not interrupt shopping or show a stale total.
    }
  }
}

if (!customElements.get('wf-application-count')) {
  customElements.define('wf-application-count', ApplicationCountComponent);
}

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    queueMicrotask(() => this.#normalizeApplicationURL());
  }

  updatedCallback() {
    super.updatedCallback();
    queueMicrotask(() => this.#normalizeApplicationURL());
  }

  // Older bookmarks can contain multiple applications. Keep their first
  // choice and refresh the section so the products, radio and intro agree.
  #normalizeApplicationURL() {
    if (!this.isConnected || !this.hasAttribute('data-single-application')) return;
    if (this.closest('results-list')?.dataset.fallbackCollectionUrl) return;

    const url = new URL(window.location.href);
    const values = url.searchParams.getAll(APPLICATION_FILTER).flatMap((value) => value.split(','));
    if (!values.length) return;

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const group = getApplicationGroup(this, values[0]).split(',');
    const current = url.searchParams.getAll(APPLICATION_FILTER);
    if (current.length === group.length && current.every((value, index) => value === group[index])) return;
    url.searchParams.delete(APPLICATION_FILTER);
    for (const value of group) url.searchParams.append(APPLICATION_FILTER, value);
    facetsForm.updateFiltersByURL(url.toString(), { replace: true });
  }

  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Fires immediately on pointerdown so a quick tap triggers the prefetch.
   * @param {PointerEvent} event
   */
  prefetchPageImmediate(event) {
    if (!(event.target instanceof HTMLElement)) return;
    this.#prefetchOption(event.target);
  }

  /**
   * Reads `checked` at call time, so callers must invoke it before the toggle
   * for the predicted post-toggle URL to be correct.
   * @param {HTMLElement} optionElement
   */
  #prefetchOption(optionElement) {
    // Fallback selections open the collection page rather than this search section.
    if (this.closest('results-list')?.dataset.fallbackCollectionUrl) return;

    const form = this.closest('form');
    if (!form) return;

    const option = optionElement.closest('.wf-application-filter__option') ?? optionElement;
    const inputElement = option instanceof HTMLInputElement ? option : option.querySelector('input');
    if (!(inputElement instanceof HTMLInputElement) || inputElement.disabled) return;

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const formData = new FormData(form);
    if (inputElement.type === 'radio') {
      if (inputElement.checked) return;
      formData.set(inputElement.name, inputElement.value);
    } else if (!inputElement.checked) {
      formData.append(inputElement.name, inputElement.value);
    }

    const urlParameters = facetsForm.createURLParameters(formData);
    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked && inputElement.type !== 'radio') {
      url.searchParams.delete(inputElement.name, inputElement.value);
    }

    sectionRenderer.getSectionHTML(this.sectionId, true, getCleanCatalogueURL(url, { fromForm: true })).catch(() => {});
  }

  /**
   * @param {PointerEvent} event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;
    this.#prefetchOption(event.target);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked && input.value !== '');
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  /** @type {string} */
  currency;
  /** @type {string} */
  moneyFormat;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('focusout', this.#onFocusOut);
    this.currency = this.dataset.currency ?? 'USD';
    this.moneyFormat = this.#extractMoneyPlaceholder(this.dataset.moneyFormat ?? '{{amount}}');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
    this.removeEventListener('focusin', this.#onFocusIn);
    this.removeEventListener('focusout', this.#onFocusOut);
  }

  /**
   * Marks the focused price input so a section morph triggered by the other price input
   * refreshes server-owned attributes without clobbering digits the shopper is still typing.
   * skipsValueUpdate wants the marker on both nodes plus live focus; PRESERVED_ATTRIBUTES
   * supplies the incoming node's copy, and it ignores empty values, hence 'true' and not ''.
   * @param {FocusEvent} event
   */
  #onFocusIn = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    event.target.setAttribute('data-skip-value-update', 'true');
  };

  /**
   * Clears the marker when the edit ends. Hygiene rather than load-bearing: skipsValueUpdate
   * requires live focus too, so a marker left behind is inert once focus moves on.
   * `change` fires before `focusout`, so the typed value is already applied by this point.
   * @param {FocusEvent} event
   */
  #onFocusOut = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    event.target.removeAttribute('data-skip-value-update');
  };

  /**
   * Extracts the placeholder from a money format string, removing currency symbols.
   * @param {string} format - The money format (e.g., "${{amount}}", "{{amount}} USD")
   * @returns {string} Just the placeholder (e.g., "{{amount}}")
   */
  #extractMoneyPlaceholder(format) {
    const match = format.match(/{{\s*\w+\s*}}/);
    return match ? match[0] : '{{amount}}';
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
  }

  /**
   * Parses a formatted money value into minor units
   * displayValue can come from user input or API response
   * @param {string} displayValue - The display value (e.g., "10.50" for USD, "9,50" for EUR, "1000" for JPY)
   * @param {string} currency - The currency code
   * @returns {number} The value in minor units
   */
  #parseDisplayValue(displayValue, currency) {
    return convertMoneyToMinorUnits(displayValue, currency) ?? 0;
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const { currency, moneyFormat } = this;
    // Parse the user's input value using currency-aware parsing
    const value = this.#parseDisplayValue(input.value, currency);

    // data-min and data-max now contain raw minor unit values (not formatted)
    const min = this.#parseDisplayValue(input.getAttribute('data-min') ?? '0', currency);
    const max = this.#parseDisplayValue(input.getAttribute('data-max') ?? '0', currency);

    if (value < min) {
      input.value = formatMoney(min, moneyFormat, currency);
    } else if (value > max) {
      input.value = formatMoney(max, moneyFormat, currency);
    }
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        if (input.type === 'radio') {
          input.checked = input.value === '';
        } else {
          input.checked = false;
          input.value = '';
        }
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {SearchUpdateEvent | CollectionUpdateEvent} event
   */
  #handleFilterUpdate = (/** @type {SearchUpdateEvent | CollectionUpdateEvent} */ event) => {
    // Ignore events from other sections (e.g. predictive search in header)
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('.shopify-section');
    const mySection = this.closest('.shopify-section');
    if (eventSection && mySection && eventSection !== mySection) return;

    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      const filters =
        event instanceof SearchUpdateEvent ? event.search?.productFilters ?? [] : event.productFilters ?? [];
      clearButton.classList.toggle('facets__clear--active', filters.length > 0);
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    if (this.hasAttribute('data-wf-best-sellers-remove')) {
      facetsForm.setBestSellers(false);
      return;
    }

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {SearchUpdateEvent | CollectionUpdateEvent} event
   */
  #handleFilterUpdate = (/** @type {SearchUpdateEvent | CollectionUpdateEvent} */ event) => {
    // Ignore events from other sections (e.g. predictive search in header)
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('.shopify-section');
    const mySection = this.closest('.shopify-section');
    if (eventSection && mySection && eventSection !== mySection) return;

    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      const activeClass = this.getAttribute('active-class') || 'active';
      const filters =
        event instanceof SearchUpdateEvent ? event.search?.productFilters ?? [] : event.productFilters ?? [];
      clearButton.classList.toggle(activeClass, filters.length > 0);
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        const span = document.createElement('span');
        span.className = 'visually-hidden';
        span.textContent = inputElement.getAttribute('aria-label') ?? '';
        return (swatch?.outerHTML ?? '') + span.outerHTML;
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    const filterStyle = this.dataset.filterStyle;

    facetStatus.classList.remove('facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (filterStyle === 'horizontal' && checkedInputElementsCount === 1) {
      facetStatus.textContent = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const currency = facetStatus.dataset.currency || '';
    const minInputNum = this.#parseCents(minInputValue, '0', currency);
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax, currency);
    facetStatus.innerHTML = `<bdi>${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}</bdi>`;
  }

  /**
   * Parses a decimal number as minor units (cents for most currencies, but adjusted for zero-decimal currencies)
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid (formatted string like "11,400")
   * @param {string} currency - The currency code (e.g., 'USD', 'JPY', 'KRW')
   * @returns {number} The money value in minor units
   */
  #parseCents(value, fallback = '0', currency = '') {
    // Try to parse the value
    const result = convertMoneyToMinorUnits(value, currency);
    if (result !== null) return result;

    // Fall back to parsing the fallback string (which may have formatting like "11,400")
    const fallbackResult = convertMoneyToMinorUnits(fallback, currency);
    if (fallbackResult !== null) return fallbackResult;

    // Last resort: clean and parse as integer
    const cleanFallback = fallback.replace(/[^\d]/g, '');
    return parseInt(cleanFallback, 10) || 0;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const format = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return formatMoney(moneyValue, format, currency);
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}
